<template>
  <div class="c-markdown-message" v-html="renderedMarkdownMessage" />
</template>

<script setup>
import sanitizeHtml from 'sanitize-html'
import { defaultHtmlSupport } from '@convenia/components/CBigText/content/config/static'
import { computed, defineProps } from 'vue'

const props = defineProps({
  message: {
    type: String,
    default: ''
  }
})

const extractCodeBlocks = (value = '') => {
  const codeBlocks = []
  const text = value.replace(/```([\w-]+)?\n([\s\S]*?)```/g, (match, lang, code) => {
    const token = `@@CODE_BLOCK_${codeBlocks.length}@@`
    codeBlocks.push({ lang, code })
    return token
  })

  return { text, codeBlocks }
}

const extractInlineCode = (value = '') => {
  const inlineCodes = []
  const text = value.replace(/`([^`]+)`/g, (match, code) => {
    const token = `@@INLINE_CODE_${inlineCodes.length}@@`
    inlineCodes.push(code)
    return token
  })

  return { text, inlineCodes }
}

const extractLinks = (value = '') => {
  const links = []
  const text = value.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (match, label, url) => {
    const token = `@@LINK_${links.length}@@`
    links.push({ label, url })
    return token
  })

  return { text, links }
}

const escapeHtml = (value = '') => {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

const buildBlocks = (value = '') => {
  const blocks = value.split(/\n{2,}/)
  const htmlBlocks = blocks.map((block) => {
    const lines = block.split(/\r?\n/)
    const trimmed = block.trim()

    if (!trimmed) return ''
    if (/^@@CODE_BLOCK_\d+@@$/.test(trimmed)) return trimmed

    const isUnorderedList = lines.every(line => /^\s*[-*+]\s+/.test(line))
    const isOrderedList = lines.every(line => /^\s*\d+\.\s+/.test(line))

    if (isUnorderedList) {
      const items = lines.map(line => line.replace(/^\s*[-*+]\s+/, ''))
      return `<ul>${items.map(item => `<li>${item}</li>`).join('')}</ul>`
    }

    if (isOrderedList) {
      const items = lines.map(line => line.replace(/^\s*\d+\.\s+/, ''))
      return `<ol>${items.map(item => `<li>${item}</li>`).join('')}</ol>`
    }

    return `<p>${lines.join('<br>')}</p>`
  })

  return htmlBlocks.filter(Boolean).join('')
}

const sanitizeUrl = (url = '') => {
  const normalized = url.trim().toLowerCase()
  if (!normalized) return null
  const isSafe = /^(https?:\/\/|mailto:|tel:)/i.test(normalized)
  return isSafe ? normalized : null
}

const restoreLinks = (value = '', links = []) => {
  return links.reduce((acc, link, index) => {
    const token = `@@LINK_${index}@@`
    const url = sanitizeUrl(link.url)
    const label = escapeHtml(link.label)
    const replacement = url
      ? `<a href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer">${label}</a>`
      : label
    return acc.replace(token, replacement)
  }, value)
}

const restoreInlineCode = (value = '', inlineCodes = []) => {
  return inlineCodes.reduce((acc, code, index) => {
    const token = `@@INLINE_CODE_${index}@@`
    const escaped = escapeHtml(code)
    return acc.replace(token, `<code>${escaped}</code>`)
  }, value)
}

const restoreCodeBlocks = (value = '', codeBlocks = []) => {
  return codeBlocks.reduce((acc, block, index) => {
    const token = `@@CODE_BLOCK_${index}@@`
    const escaped = escapeHtml(block.code)
    const langClass = block.lang ? ` class="language-${escapeHtml(block.lang)}"` : ''
    const replacement = `<pre class="markdown-code"><code${langClass}>${escaped}</code></pre>`
    return acc.replace(token, replacement)
  }, value)
}

const sanitizeMarkdownHtml = (html = '') => {
  if (!html) return ''

  const allowedContent = defaultHtmlSupport.allow?.[0] || {
    name: [],
    attributes: {},
    styles: {},
  }
  const allowedAttributes = Object.entries(allowedContent.attributes || {})
    .filter(([ , value ]) => value)
    .map(([ key ]) => key)
  const allowedStyles = Object.entries(allowedContent.styles || {})
    .filter(([ , value ]) => value)
    .reduce((acc, [ key ]) => {
      acc[key] = [ /^[^<>]+$/ ]
      return acc
    }, {})
  const allowedTags = [
    ...new Set([
      ...(allowedContent.name || []),
      'br',
      'code',
      'pre',
      'del',
    ])
  ]

  return sanitizeHtml(html, {
    allowedTags,
    allowedAttributes: {
      '*': allowedAttributes,
      a: [ 'href', 'target', 'rel', 'download' ],
      code: [ 'class' ],
      pre: [ 'class' ]
    },
    allowedStyles: {
      '*': allowedStyles
    },
    allowedSchemes: [ 'http', 'https', 'mailto', 'tel' ],
    allowProtocolRelative: false,
    allowedClasses: {
      code: [ /^language-[a-z0-9-]+$/i ],
      pre: [ 'markdown-code' ]
    }
  })
}

const toMarkdownHtml = (rawMessage = '') => {
  if (!rawMessage) return ''

  const { text: withoutBlocks, codeBlocks } = extractCodeBlocks(rawMessage)
  const { text: withoutInline, inlineCodes } = extractInlineCode(withoutBlocks)
  const { text: withoutLinks, links } = extractLinks(withoutInline)

  let html = escapeHtml(withoutLinks)

  html = html
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/__([^_]+)__/g, '<strong>$1</strong>')
    .replace(/~~([^~]+)~~/g, '<del>$1</del>')
    .replace(/\*([^*]+)\*/g, '<em>$1</em>')
    .replace(/_([^_]+)_/g, '<em>$1</em>')

  html = buildBlocks(html)
  html = restoreLinks(html, links)
  html = restoreInlineCode(html, inlineCodes)
  html = restoreCodeBlocks(html, codeBlocks)

  return sanitizeMarkdownHtml(html)
}

const hasMarkdown = computed(() => {
  if (!props.message) return false
  const markdownPattern = /(\*\*|__|~~|`|```|\[[^\]]+\]\([^)]+\)|(^|\n)\s*[-*+]\s+|(^|\n)\s*\d+\.\s+)/
  return markdownPattern.test(props.message)
})

const renderedMarkdownMessage = computed(() => {
  if (!hasMarkdown.value) return props.message
  const fallbackMessage = props.files?.length > 1
    ? 'Documentos anexados'
    : 'Documento anexado'
  return toMarkdownHtml(props.message || fallbackMessage)
})

</script>

<script>
export default {
  name: 'CMarkdownMessage',
};
</script>

<style lang='scss'>
$chat-bubble-message-color: #868CA1;
.c-markdown-message {
  color: $chat-bubble-message-color;
  & > p {
    font-size: 14px;
    font-family: var(--base-font-family);
    color: $chat-bubble-message-color;

    margin: 0 0 8px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  & > ul,
  & > ol {
    margin: 0 0 8px;
    padding-left: 18px;
  }

  & > ul:last-child,
  & > ol:last-child {
    margin-bottom: 0;
  }

  & li {
    font-size: 14px;
    margin: 4px 0;
  }

  & code {
    font-size: 14px;
    font-family: var(--mono-font-family, 'Courier New', monospace);
    padding: 2px 4px;
    border-radius: 4px;
    background: rgba(0, 0, 0, 0.06);
  }

  & pre {
    font-size: 14px;
    margin: 0 0 8px;
    padding: 10px 12px;
    border-radius: 8px;
    background: rgba(0, 0, 0, 0.06);
    overflow: auto;
  }

  & pre > code {
    font-size: 14px;
    padding: 0;
    background: transparent;
    display: block;
    white-space: pre;
  }

  & a {
    font-size: 14px;
    color: var(--color-primary-base);
    text-decoration: underline;
  }
}
</style>
